import { SelectComponent } from "@/components/select/Select";
import DatePicker from "@/components/date-picker";
import { InputField } from "@/components/input-field/InputField";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { AppColors } from "@/styles/colors";
import { Button, Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { handleTonChangeInInput } from "@/utils/query-string";
import dayjs from "dayjs";


interface OptionItem {
  label: string;
  value: string | number;
}

interface Props {
  index: number;
  control: any;
  granoOptions: OptionItem[];
  disableGrano?: boolean;
  cultivosFieldsLength: number;
  loadingCreate: boolean;
  removeCultivo: (index: number) => void;
  handleAddCultivo: () => void;
  hideAddButton?: boolean;
  hideDeleteButton?: boolean;
}

export const ProductPlanificationCultivoCard = ({
  index,
  control,
  granoOptions,
  disableGrano,
  cultivosFieldsLength,
  loadingCreate,
  removeCultivo,
  handleAddCultivo,
  hideAddButton,
  hideDeleteButton,
}: Props) => {
  const disableDelete = cultivosFieldsLength <= 1 || loadingCreate;
  const showDelete = !hideDeleteButton && cultivosFieldsLength > 1;

  return (
    <Card.Root maxW="full" borderRadius="8px">
      <Card.Body
        paddingX="48px"
        paddingY="24px"
        borderRadius="8px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
      >
        <Flex justifyContent="space-between" alignItems="center" marginBottom="16px">
          <Text fontWeight={700} fontSize="16px">
            {`Cultivo ${index + 1}`}
          </Text>

          {showDelete && (
            <Trash2
              cursor={disableDelete ? "not-allowed" : "pointer"}
              style={{ pointerEvents: disableDelete ? "none" : "auto" }}
              onClick={() => {
                if (!disableDelete) removeCultivo(index);
              }}
              aria-label="Eliminar cultivo"
            />
          )}
        </Flex>

        <Grid templateColumns="repeat(4, 1fr)" gap="16px" alignItems="end">
          <GridItem colSpan={1}>
            <SelectComponent
              control={control}
              name={`cultivos.${index}.idGrano`}
              labelText="Cultivo"
              placeholder="Seleccionar"
              items={granoOptions}
              disabled={loadingCreate || Boolean(disableGrano)}
              required
              minHeight="40px"
              clearable
              fullWidth
              menuMaxHeight="250px"
              size="xs"
            />
          </GridItem>

          <GridItem colSpan={1}>
            <InputField
              control={control}
              inputName={`cultivos.${index}.hectareasAsembrar`}
              labelText="Ha a sembrar"
              placeholder="0"
              minHeight="40px"
              disabled={loadingCreate}
              type="text"
              required
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
            />
          </GridItem>

            <GridItem colSpan={1}>
            <InputField
              control={control}
              inputName={`cultivos.${index}.consumoPropio`}
              labelText="Consumo propio (TT)"
              placeholder="0"
              minHeight="40px"
              disabled={loadingCreate}
              type="text"
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <InputField
              control={control}
              inputName={`cultivos.${index}.consumoPropioPicado`}
              labelText="Consumo propio picado (Has)"
              placeholder="0"
              minHeight="40px"
              disabled={loadingCreate}
              type="text"
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <InputField
              control={control}
              inputName={`cultivos.${index}.toneladasRinde`}
              labelText="Toneladas rinde"
              placeholder="0"
              minHeight="40px"
              disabled={loadingCreate}
              type="text"
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Field.Root>
              <Field.Label>
                <Text display="flex" gap="4px" fontWeight={600}>
                  Costo estimado (USD)
                </Text>
              </Field.Label>
              <InputCurrency
                control={control}
                name={`cultivos.${index}.costoEstimado`}
                height="40px"
                maxLength={20}
                prefix="U$D "
                disabled={loadingCreate}
              />
            </Field.Root>
          </GridItem>

          <GridItem colSpan={1}>
            <DatePicker
              control={control}
              name={`cultivos.${index}.fechaEstimadaSiembra`}
              labelText="Fecha estimada de siembra"
              height="40px"
              minHeight="40px"
              disablePortal={false}
              maxYear={new Date().getFullYear() + 2}
              minDate={dayjs()}
              disabled={loadingCreate}
            />
          </GridItem>

        
        </Grid>

        {index === cultivosFieldsLength - 1 && !hideAddButton && (
          <Flex marginTop="20px">
            <Button
              onClick={handleAddCultivo}
              disabled={loadingCreate}
              paddingX="12px"
              height="32px"
              borderRadius="6px"
              bgColor={AppColors.GREEN_PRIMARY}
              opacity={loadingCreate ? 0.6 : 1}
            >
              <Text fontWeight={600} fontSize="14px" color="white">
                Guardar y agregar otro cultivo
              </Text>
            </Button>
          </Flex>
        )}
      </Card.Body>
    </Card.Root>
  );
};
